import { flow, types } from 'mobx-state-tree';
import { apiService } from '@services/apiService';
import { withAlertStore } from '@stores/alert/withAlertStore';
import { withMainStore } from '@stores/main/withMainStore';
import { withProfileStore } from '@stores/profile/withProfileStore';
import { filterReqData } from '@utils/prepareData';
import { IChangeStatus } from '@/types/common';
import { IProductFormWithImageFile } from '@forms/ProductForm/types';
import {
  IBusinessListProductData,
  IProductsCategoryData,
  IProductFormFieldsReq,
  IBusinessProductData,
  IDeleteProductData,
  ProductUploadCSVForm,
} from './types';
import { MAX_MODARATED_PRODUCTS } from './constants';
import { withBrandsStore } from '../brands/withBrandsStore';

const { model, maybeNull, frozen } = types;

export const ProductsStore = model('ProductsStore', {
  productsList: maybeNull(frozen<IBusinessListProductData[]>()),
  productsCategories: maybeNull(frozen<IProductsCategoryData[]>()),
  product: maybeNull(frozen<IBusinessProductData>()),
})
  .extend(withAlertStore)
  .extend(withMainStore)
  .extend(withProfileStore)
  .extend(withBrandsStore)
  .views((self) => ({
    get isAbleToLoadSCV(): boolean {
      return (
        (self.productsList?.filter(({ status }) => status === 'Pending' || status === 'Revise')
          .length || 0) < MAX_MODARATED_PRODUCTS
      );
    },
  }))
  .actions((self) => ({
    setProductsList(data: IBusinessListProductData[]) {
      self.productsList = data;
      return data;
    },
    setProduct(data: IBusinessProductData) {
      self.product = data;
      return data;
    },
    setProductsCategories(data: IProductsCategoryData[] | null) {
      self.productsCategories = data;
      return data;
    },
    deleteProductsHandler(id: string) {
      if (self.productsList) {
        self.productsList = self.productsList.filter((item) => item.id !== id);
      }
    },
  }))
  .actions((self) => ({
    getProductsList: flow(function* (brandId: string) {
      return yield apiService
        .get(`product-list/${brandId}?limit=-1`)
        .then(({ data: { data } }) => {
          return self.setProductsList(data);
        })
        .catch(self.mainStore.errorHandling);
    }),
    getProductsCategories: flow(function* () {
      return yield apiService
        .get(`product/categories`)
        .then(({ data: { data } }) => {
          return self.setProductsCategories(data);
        })
        .catch(self.mainStore.errorHandling);
    }),
    getProduct: flow(function* ({ id, status }: IChangeStatus) {
      return yield apiService
        .get(`product/show/${id}?status=${status}`)
        .then(({ data: { data } }) => {
          return self.setProduct(data);
        })
        .catch(self.mainStore.errorHandling);
    }),
    addProduct: flow(function* (data: IProductFormWithImageFile) {
      const { imageUrlFile, categories, isNeedValidation, ...filtred } = data;
      const newData: IProductFormFieldsReq = filterReqData({
        ...filtred,
        categories: categories.map(({ id }) => id).join(','),
      });
      return yield apiService.post('product/create', newData).catch(self.mainStore.errorHandling);
    }),
    editProduct: flow(function* (data: IBusinessProductData & IProductFormWithImageFile) {
      const { imageUrlFile, categories, isNeedValidation, ...filtred } = data;
      const newData: IProductFormFieldsReq = filterReqData({
        ...filtred,
        categories: categories.map(({ id }) => id).join(','),
      });

      return yield apiService
        .patch(`product/update/${data.id}?isNeedValidation=${isNeedValidation}`, newData)
        .then(({ data: { data } }) => {
          return self.setProduct(data);
        })
        .catch(self.mainStore.errorHandling);
    }),
    changeStatus: flow(function* ({ id, status }: IChangeStatus) {
      return yield apiService
        .patch(`product/update-status/${id}`, { status })
        .then(({ data: { data } }) => {
          return self.setProduct(data);
        })
        .catch(self.mainStore.errorHandling);
    }),
    deleteProduct: flow(function* ({ id, brandId }: IDeleteProductData) {
      return yield apiService
        .delete(`product/delete/${id}?brandId=${brandId}`)
        .then(() => self.deleteProductsHandler(id))
        .catch(self.mainStore.errorHandling);
    }),
    uploadSCV: flow(function* (data: ProductUploadCSVForm) {
      return yield apiService
        .post(`import`, data)
        .then(({ data: { data } }) => {
          const brand = self.brandsStore.brand;
          if (brand) self.brandsStore.setBrand({ ...brand, productsCount: data.length });
          return self.setProductsList(data);
        })
        .catch(({ response }) => {
          const msg = response?.data.message || 'Application error';
          throw new Error(msg);
        });
    }),
  }));
